import { ArrowRight, BadgeCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { CODE_LENGTH, LIMITS, type JoinResponse } from '@amg/protocol';
import { api, ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { CodeInput } from './CodeTicket';
import { Button } from './ui/Button';
import { Field, FormError, Input } from './ui/Field';

const NAME_KEY = 'amg-guest-name';

function rememberedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Enter a code (and, for guests, a name). Submitting only asks to join: the host still has to
 * let the person in.
 */
export function JoinForm({
  onJoined,
  className,
  autoFocus = false,
  submitVariant = 'primary',
}: {
  onJoined: (meetingId: string) => void;
  className?: string;
  autoFocus?: boolean;
  /** Secondary where joining is not the page's main task (staff home). */
  submitVariant?: 'primary' | 'secondary';
}) {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [name, setName] = useState(rememberedName);
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const symbols = code.replace(/-/g, '').length;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const badCode = symbols !== CODE_LENGTH ? 'Enter all 10 characters of the code.' : null;
    const badName = !user && !name.trim() ? 'Enter your name so the host knows who is joining.' : null;
    setCodeError(badCode);
    setNameError(badName);
    if (badCode || badName) return;

    setBusy(true);
    try {
      const res = await api<JoinResponse>('/api/join', { body: { code, name: user ? undefined : name.trim() } });
      if (!user) {
        try {
          localStorage.setItem(NAME_KEY, name.trim());
        } catch {
          /* not remembered */
        }
      }
      onJoined(res.meetingId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_code') setCodeError(err.message);
      else if (err instanceof ApiError && err.code === 'name_required') setNameError(err.message);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className={cn('flex flex-col gap-5', className)}>
      <FormError>{error}</FormError>
      <Field label="Connection code" hint="10 characters. Dashes and capitals are optional." error={codeError}>
        {(p) => (
          <CodeInput
            {...p}
            value={code}
            onValueChange={(v) => {
              setCode(v);
              if (codeError) setCodeError(null);
            }}
            placeholder="XXX-XXXX-XXX"
            autoFocus={autoFocus}
          />
        )}
      </Field>
      {user ? (
        <p className="flex items-center gap-2 rounded-md border border-line bg-inset/60 px-3.5 py-3 text-compact text-ink-2">
          <BadgeCheck aria-hidden className="size-4 shrink-0 text-success-text" />
          <span>
            You'll join as <strong className="font-semibold text-ink">{user.name}</strong>, shown to the host as verified AMG staff.
          </span>
        </p>
      ) : (
        <Field label="Your name" hint="The host sees this before letting you in." error={nameError}>
          {(p) => (
            <Input
              {...p}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              autoComplete="name"
              maxLength={LIMITS.nameMax}
              placeholder="e.g. Priya Sharma"
            />
          )}
        </Field>
      )}
      <Button
        type="submit"
        variant={submitVariant}
        size={submitVariant === 'primary' ? 'xl' : 'lg'}
        block
        loading={busy}
        iconRight={<ArrowRight aria-hidden className="size-[1.125rem]" />}
      >
        {busy ? 'Checking the code…' : 'Join session'}
      </Button>
    </form>
  );
}
